// DRAFT direct port from POC.
// TODO: rewrite using Bridebook-next utils
import type { IUser } from '@bridebook/models/source/models/Users.types';
import type { IWeddingEvent } from '@bridebook/models/source/models/Weddings/Events.types';
import { type IGuest, Rsvp } from '@bridebook/models/source/models/Weddings/Guests.types';
import type { IPreference } from '@bridebook/models/source/models/Weddings/Preferences.types';
import type { IPreferenceVenue } from '@bridebook/models/source/models/Weddings/Preferences/venue.types';
import type { ISupplier } from '@bridebook/models/source/models/Weddings/Suppliers.types';
import type { IDatePicker, IWedding } from '@bridebook/models/source/models/Weddings.types';
import { weddingDateAnchor, weddingDateFromPicker } from './wedding-date';
import { PLANNER_SUPPLIER_TYPES } from '@bridebook/web/lib/ai-planner/supplier-types';
import { getOverviewRsvpResponse } from '@bridebook/web/lib/rsvp/rsvp-helpers';
import { summariseProfile, type WeddingProfileSummary } from './wedding-profile';

export type WeddingContext = {
  user: IUser | undefined;
  wedding: IWedding | undefined;
  suppliers: Record<string, ISupplier> | undefined;
  guestCategories: Record<string, string>;
  guests: Record<string, IGuest> | undefined;
  events: Record<string, IWeddingEvent> | undefined;
  preferences: Record<string, IPreference> | undefined;
  weddingId: string | undefined;
  // The couple's checklist, real or projected. Null while not yet known.
  checklist: ResolvedChecklist | null;
  // The couple's budget breakdown, real or projected. Null while not yet known.
  budgetProjection: BudgetProjection | null;
  // The country template the checklist resolved against.
  blueprint: PlanningBlueprint | null;
  isLoading: boolean;
};
import { buildGroupTimings } from './planning/blueprint/group-timings';
import type { BudgetAssumptions, BudgetLine, BudgetProjection } from './planning/budget/types';
import type { PlanningBlueprint, ResolvedChecklist } from './planning/blueprint/types';
import { computePhaseProgress, type PhaseProgress } from './planning/compute-phase-progress';
import { getPeriodName } from './planning/period-name';
import { DETAILED_GROUPS, selectUpcomingGroups } from './planning/select-upcoming-groups';

/**
 * One supplier as the model reads it. `enquired` and `visitDate` are set only when the
 * record has them. The couple's notes are left out: they can be personal, and the model
 * does not need them.
 */
export type SupplierRow = {
  id: string;
  name: string;
  type: string;
  enquired?: true;
  visitDate?: string;
};

// How many saved suppliers the summary sends. A long shortlist is browsing, not a plan,
// and every row adds to the request size.
export const SHORTLIST_LIMIT = 30;

// Who is writing: the wedding's primary user, or a collaborator who need not be either partner.
export type WeddingWriter = 'owner' | 'collaborator';

export type WeddingContextSummary = {
  weddingId: string | undefined;
  // Set by the client from the wedding's users; absent until they are known.
  writer?: WeddingWriter;
  profile: WeddingProfileSummary | null;
  budget: {
    // True when there are no cost items and this comes from country targets
    projected: boolean;
    target: number | null;
    totalEstimate: number;
    totalActual: number;
    itemCount: number;
    // Every category with its share and estimate, largest first.
    allocation: BudgetLine[];
    // What a projection had to assume. Null for a real budget.
    assumptions: BudgetAssumptions | null;
  } | null;
  checklist: {
    // True when the couple has no checklist and this comes from the country template
    projected: boolean;
    initializedAt: number | null;
    // The wedding year is undecided, so every date here is an assumption.
    approximateDate: boolean;
    completedGroupIds: string[];
    total: number;
    done: number;
    phaseProgress: PhaseProgress[];
    upcomingTasks: { name: string; period: string; done: boolean }[];
    // One row per group with open tasks. The card reads intro and subtasks from the
    // browser store by group id, so they are not sent.
    upcomingGroups: {
      groupId: string;
      groupName: string;
      supplierType: string | null;
      period: string;
      // The calendar month the group starts in, "YYYY-MM".
      month?: string | null;
      isOverdue: boolean;
      doneCount: number;
      totalCount: number;
      // Outstanding task names, on the nearest groups only.
      openTasks?: string[];
      // A custom main task of the couple's, which heads its own group.
      custom?: boolean;
    }[];
  } | null;
  guests: {
    total: number;
    attending: number;
    declined: number;
    pending: number;
    // Guests who answered differently across events — neither a yes nor a no.
    mixedResponse: number;
    dietaryRequirements: string[];
    categories: Record<string, string>;
  } | null;
  suppliers: {
    // `id` is what the supplier client actions use.
    booked: SupplierRow[];
    // The newest saved suppliers that are not booked yet, up to the shortlist cap.
    shortlisted: SupplierRow[];
    // How many saved suppliers there are in all, cap or no cap.
    shortlistedTotal: number;
    missingCategories: string[];
  } | null;
  events:
    | {
        // The id the event client actions use.
        id: string;
        name: string;
        // Custom events only; the main event's date is the wedding date.
        date: string | undefined;
        type: string;
        startTime: string | undefined;
        endTime: string | undefined;
        // Whether the event shows on the couple's wedding website.
        featured: boolean;
        invitedGuestCount: number;
      }[]
    | null;
  preferences: {
    venueStyles: string[];
    venueTypes: string[];
    venueMustHaves: string[];
  } | null;
};

// A safety cap, not a ranking limit: real templates have fewer groups than this.
const MAX_CANDIDATE_GROUPS = 80;

const completedGroups = (tasks: readonly { group: string; done: boolean }[]): string[] => {
  const outstanding = new Set(tasks.filter((task) => !task.done).map((task) => task.group));
  const all = new Set(tasks.map((task) => task.group));
  return [...all].filter((group) => !outstanding.has(group));
};

// The wedding as a JS Date, so period labels can show a real month. A season uses its first
// month and an unknown month uses January, as the checklist does. Null without a year.
const weddingDateToJs = (date: IDatePicker | undefined): Date | null => {
  const parsed = weddingDateFromPicker(date);
  if (!parsed) {
    return null;
  }
  const anchor = weddingDateAnchor(parsed);
  return new Date(Date.UTC(anchor.year, (anchor.month ?? 1) - 1, anchor.day));
};

// The browser store holds milliseconds; a Firestore record holds a Timestamp. Anything
// else sorts last.
const createdAtMs = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
};

// The part of the summary that capabilities and chips read: the profile.
export type WeddingProfileCarrier = Pick<WeddingContextSummary, 'profile'>;

export const summarisePreferences = (
  venue: Pick<IPreferenceVenue, 'styles' | 'types' | 'mustHaves'> | undefined,
): WeddingContextSummary['preferences'] =>
  venue
    ? {
        venueStyles: venue.styles ?? [],
        venueTypes: venue.types ?? [],
        venueMustHaves: venue.mustHaves ?? [],
      }
    : null;

// Resolved, so a couple with no budget still gets an allocation. Null while not yet known.
export const summariseBudget = (
  resolvedBudget: BudgetProjection | null,
): WeddingContextSummary['budget'] =>
  resolvedBudget
    ? {
        projected: resolvedBudget.projected,
        // An assumed total is not the couple's target. Shown as one, it would conflict with
        // the figures the coach quotes.
        target: resolvedBudget.assumptions?.targetAssumed ? null : resolvedBudget.target,
        totalEstimate: resolvedBudget.allocation.reduce((sum, line) => sum + line.estimate, 0),
        totalActual: resolvedBudget.allocation.reduce((sum, line) => sum + line.actual, 0),
        itemCount: resolvedBudget.allocation.length,
        allocation: resolvedBudget.allocation,
        assumptions: resolvedBudget.assumptions,
      }
    : null;

// Resolved, so a couple with no checklist still gets a plan. Null while not yet known.
export const summariseChecklist = (
  resolved: ResolvedChecklist | null,
  blueprint: PlanningBlueprint | null,
  date: IDatePicker | undefined,
): WeddingContextSummary['checklist'] => {
  if (!(resolved && blueprint)) {
    return null;
  }
  const items = resolved.tasks;
  const sorted = [...items].filter((t) => !t.done).sort((a, b) => a.order - b.order);
  const weddingJsDate = weddingDateToJs(date);
  const timings = buildGroupTimings(items);

  const upcomingGroups = selectUpcomingGroups(items, {
    // Every group with open tasks, not only the earliest few.
    count: MAX_CANDIDATE_GROUPS,
    groups: blueprint.groups,
    weddingDate: weddingJsDate,
    projected: resolved.projected,
  }).map(({ openTasks, ...g }, index) => ({
    ...g,
    period: getPeriodName(weddingJsDate, g.period),
    // Open task names only on the nearest groups; the rest carry counts only.
    ...(index < DETAILED_GROUPS ? { openTasks } : {}),
  }));

  return {
    projected: resolved.projected,
    initializedAt: resolved.initializedAt,
    approximateDate: resolved.approximateDate,
    total: items.length,
    done: items.filter((t) => t.done).length,
    completedGroupIds: completedGroups(items),
    phaseProgress: computePhaseProgress(items, timings),
    upcomingTasks: sorted.slice(0, 10).map((t) => ({
      name: t.name,
      period: getPeriodName(weddingJsDate, t.period),
      done: t.done,
    })),
    upcomingGroups,
  };
};

export const summariseGuests = (
  guests: WeddingContext['guests'],
  events: WeddingContext['events'],
  categories: WeddingContext['guestCategories'],
): WeddingContextSummary['guests'] => {
  if (!guests) {
    return null;
  }
  const items = Object.values(guests);
  const total = items.length;
  let attending = 0;
  let declined = 0;
  let pending = 0;
  let mixedResponse = 0;
  const dietarySet = new Set<string>();
  const weddingEvents = Object.values(events ?? {});

  for (const g of items) {
    const rsvp = getOverviewRsvpResponse(weddingEvents, g.invitation);
    switch (rsvp) {
      case Rsvp.YES:
        attending++;
        break;
      case Rsvp.NO:
        declined++;
        break;
      case 'mixedResponse':
        mixedResponse++;
        break;
      default:
        pending++;
    }
    if (g.dietaryOptions) {
      for (const d of g.dietaryOptions) {
        dietarySet.add(d);
      }
    }
  }

  return {
    total,
    categories,
    attending,
    declined,
    pending,
    mixedResponse,
    dietaryRequirements: [...dietarySet],
  };
};

/**
 * A saved supplier: the fields the record and the store's supplier share. `createdAt` is a
 * timestamp on one and a number on the other.
 */
export type SupplierSource = Pick<
  ISupplier,
  'id' | 'name' | 'type' | 'booked' | 'ignored' | 'enquired' | 'visitDate'
> & { createdAt?: unknown };

// Ignored suppliers are skipped. All booked ones are sent; saved ones newest first up to
// the cap, with the full count.
export const summariseSuppliers = (
  suppliers: Record<string, SupplierSource> | undefined,
): WeddingContextSummary['suppliers'] => {
  if (!suppliers) {
    return null;
  }
  const items = Object.values(suppliers).filter((s) => !s.ignored);
  const row = (s: SupplierSource): SupplierRow => ({
    id: s.id,
    name: s.name ?? s.id,
    type: s.type,
    ...(s.enquired ? { enquired: true as const } : {}),
    ...(s.visitDate ? { visitDate: s.visitDate } : {}),
  });
  const booked = items.filter((s) => s.booked).map(row);
  const saved = items
    .filter((s) => !s.booked)
    .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
  const shortlisted = saved.slice(0, SHORTLIST_LIMIT).map(row);
  const coveredTypes = new Set<string>(items.map((s) => s.type));
  const missingCategories = PLANNER_SUPPLIER_TYPES.filter((cat) => !coveredTypes.has(cat));
  return { booked, shortlisted, shortlistedTotal: saved.length, missingCategories };
};

// A guest counts as invited when their invitation includes the event, answered or not.
export const summariseEvents = (
  events: WeddingContext['events'],
  guests: WeddingContext['guests'],
): WeddingContextSummary['events'] => {
  if (!events) {
    return null;
  }
  const allGuests = Object.values(guests ?? {});
  return Object.values(events).map((e) => ({
    id: e.id,
    name: e.name,
    date: e.type === 'CustomEvent' ? e.date : undefined,
    type: e.type,
    startTime: e.time?.startTime,
    endTime: e.time?.endTime,
    featured: e.featured,
    invitedGuestCount: allGuests.filter((g) => !!g.invitation?.events?.[e.id]).length,
  }));
};

// The summary built from one assembled context.
export const serializeWeddingContext = (ctx: WeddingContext): WeddingContextSummary => ({
  weddingId: ctx.weddingId,
  profile: summariseProfile(ctx.wedding),
  budget: summariseBudget(ctx.budgetProjection),
  checklist: summariseChecklist(ctx.checklist, ctx.blueprint, ctx.wedding?.date),
  guests: summariseGuests(ctx.guests, ctx.events, ctx.guestCategories),
  suppliers: summariseSuppliers(ctx.suppliers),
  events: summariseEvents(ctx.events, ctx.guests),
  preferences: summarisePreferences(ctx.preferences?.venue as IPreferenceVenue | undefined),
});
